import React, { useState } from 'react';
import Select from 'react-select';

import { getRankRange } from '../../../helpers/getRankRange';
import { supabase } from '../../../utils/supabase';
import CountySelectTrips from '../../input/CountySelectTrips';
import GolfCourseSelect from '../../input/GolfCourseSelect';
import PostcodeSelect from '../../input/PostcodeSelect';
import LoadingScreen from '../../loading/LoadingScreen';
import GolfTripResults from './GolfTripResults';

function formatOptions(options) {
  return options.map((option) => ({ label: option, value: option }));
}

const customStyles = {
  container: (provided) => ({
    ...provided,
    flexGrow: 1,
  }),
  control: (provided) => ({
    ...provided,
    width: '100%',
  }),
};

function TripSearchCriteria() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCountrySearch, setSelectedCountrySearch] = useState(null);
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [locationType, setLocationType] = useState(null);
  const [rounds, setRounds] = useState(5);
  const [countryRankPrefix, setCountryRankPrefix] = useState(null);
  const [rankColumn, setRankColumn] = useState('');
  const [rating, setRating] = useState('Top 1 - 100');
  const [golfCourses, setGolfCourses] = useState([]); // State to store fetched golf courses
  const [selectedGolfCourse, setSelectedGolfCourse] = useState(null);
  const [postcode, setPostcode] = useState('');
  const ratings = [
    'Top 1 - 100',
    'Top 1 - 200',
    'Top 101 - 200',
    'Top 201 - 500',
    'Top 501 - 2000',
    'All Courses',
  ];
  const [maxDistance, setMaxDistance] = useState('Any Distance'); // New state for max distance
  const countries = [
    'England',
    'Scotland',
    'Ireland',
    'Wales',
    'Northern Ireland',
  ];
  const countryMapping = {
    England: 'england',
    Scotland: 'scotland',
    Ireland: 'ireland',
    'Northern Ireland': 'ni',
    Wales: 'wales',
    USA: 'usa',
  };

  // Define the options for max distance dropdown
  const distanceOptions = [
    { label: 'Any Distance', value: Infinity }, // Use Infinity for 'Any Distance'
    { label: '10km', value: 10000 },
    { label: '25km', value: 25000 },
    { label: '50km', value: 50000 },
    { label: '100km', value: 100000 },
  ];

  const [selectedDistance, setSelectedDistance] = useState(distanceOptions[0]); // Default to first option

  const handleDistanceChange = (selectedOption) => {
    setSelectedDistance(selectedOption);
    setMaxDistance(selectedOption.value); // Update maxDistance for calculations
  };

  const handleSearch = () => {
    switch (locationType) {
      case 'Country':
      case 'County':
        handleCountryOrCountySearch();
        break;
      case 'Golf Course':
      case 'Postcode':
        handleLocationSearch();
        break;
      default:
        setError('Please select a valid location type.');
        break;
    }
  };

  const handleCountryChange = (option) => {
    setSelectedCountrySearch(option);
    setCountryRankPrefix(countryMapping[option.value]);
    //console.log("On dropdown country select", option.value);
  };

  const handleCountyChange = (option) => {
    setSelectedCounty(option);
    const countryOfSelectedCounty = option.country;
    setSelectedCountrySearch({
      label: countryOfSelectedCounty,
      value: countryOfSelectedCounty,
    });
    setCountryRankPrefix(countryMapping[countryOfSelectedCounty]);
    setRankColumn(`${countryMapping[countryOfSelectedCounty]}_rank_gl`);
    //console.log("On dropdown county select", option.value);
  };

  const handleGolfCourseChange = async (option) => {
    //console.log("Course Function is running");
    try {
      setSelectedGolfCourse(option); // Store the selected golf course
      const countryOfSelectedCourse = option.country;
      setSelectedCountrySearch({
        label: countryOfSelectedCourse,
        value: countryOfSelectedCourse,
      });
      setCountryRankPrefix(countryMapping[countryOfSelectedCourse]);
      setRankColumn(`${countryMapping[countryOfSelectedCourse]}_rank_gl`);
      //console.log("RankCol in Function", rankColumn);
      //console.log("On dropdown golf course select",option.label,"Country:",countryOfSelectedCourse);
    } catch (error) {
      console.error('Error fetching course country:', error);
    }
  };

  const handlePostcodeChange = async (event) => {
    const value = event.target.value; // Extract the value from the event object
    setPostcode(value);
    try {
      // Fetch data from the postcode API
      const response = await fetch(
        `https://api.postcodes.io/postcodes/${value}`,
      );
      const data = await response.json();

      if (data.status === 200) {
        const countryFromAPI = data.result.country;
        setSelectedCountrySearch({
          label: countryFromAPI,
          value: countryFromAPI,
        });
        setCountryRankPrefix(countryMapping[countryFromAPI]);
        setRankColumn(`${countryMapping[countryFromAPI]}_rank_gl`);
      } else {
        // Handle any API error gracefully
        setError('Error fetching postcode details. Please try again later.');
      }
    } catch (err) {
      console.error('Failed to fetch postcode details:', err);
      setError(
        'Error fetching postcode details. Please check the postcode or try again later.',
      );
    }
  };

  //console.log(rounds);
  //console.log("prefix", countryRankPrefix);
  //console.log("Rank Col", rankColumn);

  //Main search function 1
  const handleCountryOrCountySearch = async () => {
    try {
      setIsLoading(true);
      let whereConditions = {};

      if (!selectedCountrySearch) {
        setError('Please select a location before proceeding.');
        setIsLoading(false);
        return;
      }

      const currentCountryRankPrefix =
        countryMapping[selectedCountrySearch.value];

      // Check if locationType is "County" and set the necessary conditions
      if (locationType === 'County') {
        if (!selectedCounty) {
          setError('Please select a county before proceeding.');
          setIsLoading(false);
          return;
        }
        whereConditions['course_county'] = selectedCounty.value;
      } else {
        whereConditions['course_country'] = selectedCountrySearch.value;
      }

      const golfCoursesQuery = supabase.from('golf_courses').select('*');
      Object.keys(whereConditions).forEach((key) => {
        golfCoursesQuery.eq(key, whereConditions[key]);
      });
      const rankColumn = `${currentCountryRankPrefix}_rank_gl`;

      golfCoursesQuery.order(rankColumn, { ascending: true });

      const { data: golfCoursesData, error: gcError } = await golfCoursesQuery;
      if (gcError) {
        throw gcError;
      }

      const { data: greenFeesData, error: gfError } = await supabase
        .from('green_fees')
        .select('*');
      if (gfError) {
        throw gfError;
      }

      let filteredCourses = golfCoursesData.sort(
        (a, b) => a[rankColumn] - b[rankColumn],
      );

      // New logic for rank range filtering with fallback
      let currentRatingIndex = ratings.indexOf(rating);
      let results = [];
      while (results.length < rounds && currentRatingIndex < ratings.length) {
        const { start, end } = getRankRange(ratings[currentRatingIndex]);
        const currentResults = filteredCourses.filter(
          (course) => course[rankColumn] >= start && course[rankColumn] <= end,
        );
        results = [...results, ...currentResults];
        currentRatingIndex++;
      }

      // Then you slice based on the number of rounds
      results = results.slice(0, rounds);

      const mergedData = results.map((course) => {
        const greenFee = greenFeesData.find(
          (gf) => gf.course_name === course.course_name,
        );
        return {
          ...course,
          green_fees: greenFee ? { ...greenFee } : {},
        };
      });

      setGolfCourses(mergedData);
      setIsLoading(false);
    } catch (error) {
      console.error('Error during search:', error);
      setError('Could not fetch golf courses');
      setGolfCourses([]);
    }
  };

  // HELPER Haversine formula to calculate distance between two points given lat/lng
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // radius of Earth in meters
    const φ1 = lat1 * (Math.PI / 180);
    const φ2 = lat2 * (Math.PI / 180);
    const Δφ = (lat2 - lat1) * (Math.PI / 180);
    const Δλ = (lon2 - lon1) * (Math.PI / 180);

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // in meters
  };

  //Main search function 1
  //Golf Course & Postcode Search Function
  //Golf Course & Postcode Search Function
  const handleLocationSearch = async () => {
    try {
      setIsLoading(true);

      let latitude, longitude;

      // If searching by Golf Course
      if (locationType === 'Golf Course') {
        if (!selectedGolfCourse) {
          setError('Please select a golf course before proceeding.');
          setIsLoading(false);
          return;
        }

        // Fetch the latitude & longitude for the selected golf course
        const { data: selectedCourseData, error: scError } = await supabase
          .from('golf_courses')
          .select('latitude, longitude')
          .eq('course_uid', selectedGolfCourse.courseuid); // Use the course_uid from the selected golf course
        if (scError) throw scError;
        latitude = selectedCourseData[0].latitude;
        longitude = selectedCourseData[0].longitude;

        // If searching by Postcode
      } else if (locationType === 'Postcode') {
        if (!postcode) {
          setError('Please provide a postcode before proceeding.');
          setIsLoading(false);
          return;
        }

        const response = await fetch(
          `https://api.postcodes.io/postcodes/${postcode}`,
        );
        const data = await response.json();

        if (data.status !== 200) {
          setError('Error fetching postcode details. Please try again later.');
          setIsLoading(false);
          return;
        }

        latitude = data.result.latitude;
        longitude = data.result.longitude;
      } else {
        setError('Invalid location type selected.');
        setIsLoading(false);
        return;
      }

      // Fetch all golf courses to calculate distance
      const { data: allGolfCourses, error: gcError } = await supabase
        .from('golf_courses')
        .select('*');

      if (gcError) throw gcError;

      // New logic for rank range filtering
      let currentRatingIndex = ratings.indexOf(rating);
      let results = [];
      while (results.length < rounds && currentRatingIndex < ratings.length) {
        const { start, end } = getRankRange(ratings[currentRatingIndex]);
        const currentResults = allGolfCourses.filter(
          (course) => course[rankColumn] >= start && course[rankColumn] <= end,
        );
        results = [...results, ...currentResults];
        currentRatingIndex++;
      }

      // Now, we calculate distances for these filtered courses and then sort them
      let distances = results
        .filter(
          (course) => course.latitude && course.longitude && course[rankColumn],
        )
        .map((course) => ({
          ...course,
          distance: calculateDistance(
            latitude,
            longitude,
            course.latitude,
            course.longitude,
          ),
        }))
        .sort((a, b) => a.distance - b.distance);
      // Filter based on max distance if it's not 'Any Distance'
      if (maxDistance !== Infinity) {
        distances = distances.filter(
          (course) => course.distance <= maxDistance,
        );
      }
      // Then you slice based on the number of rounds
      distances = distances.slice(0, rounds);

      const { data: greenFeesData, error: gfError } = await supabase
        .from('green_fees')
        .select('*');

      if (gfError) throw gfError;

      const mergedData = distances.map((course) => {
        const greenFee = greenFeesData.find(
          (gf) => gf.course_name === course.course_name,
        );
        return {
          ...course,
          green_fees: greenFee ? { ...greenFee } : {},
        };
      });

      setGolfCourses(mergedData);
      setIsLoading(false);
    } catch (error) {
      console.error('Error during location search:', error);
      setError('Could not fetch golf courses');
      setGolfCourses([]);
    }
  };

  //console.log("GC Data", golfCourses);

  return (
    <>
      <section className="relative mx-auto w-full min-h-[600px] bg-slate-100">
        <div className="bg-indigo-100 text-white pt-6 pb-12 md:pt-12 md:pb-22  mx-auto">
          <h1 className="text-indigo-900 w-11/12 text-2xl md:text-5xl font-bold max-w-5xl mx-auto px-4 md:px-0">
            UK golf trip planner — holidays, breaks and weekends
          </h1>
          <h2 className="text-indigo-900 w-11/12 text-sm md:text-xl max-w-5xl mx-auto mt-2 px-4 md:px-0">
            Plan golf holidays and weekend breaks: search by country, county,
            near a course or UK postcode. Works for England, Scotland, Wales,
            Northern Ireland and Ireland.
          </h2>
        </div>
        <div className="max-h-[425px] z-40 absolute left-0 right-0 top-32 md:top-36 mx-auto bg-indigo-700 rounded-md w-11/12 md:w-full max-w-sm md:max-w-5xl p-2 grid grid-cols-1 md:flex md:flex-wrap sm:justify-between sm:items-center space-y-2 sm:space-x-2">
          {/*Location*/}
          <div className="flex-grow sm:ml-2 mt-2 xl:ml-0">
            <label className="block text-white font-bold text-sm lg:text-base">
              Location Select:
            </label>
            <Select
              options={formatOptions([
                'Country',
                'County',
                'Golf Course',
                'Postcode',
              ])}
              onChange={(e) => setLocationType(e.value)}
              styles={customStyles}
              placeholder="Select location type"
            />
          </div>

          {locationType && (
            <div className="flex-grow">
              <label className="block text-white font-bold text-sm lg:text-base">
                Trip Base:
              </label>

              {locationType === 'Country' && (
                <Select
                  options={formatOptions(countries)}
                  styles={customStyles}
                  onChange={handleCountryChange}
                  value={selectedCountrySearch}
                />
              )}

              {locationType === 'County' && (
                <CountySelectTrips onChange={handleCountyChange} />
              )}
              {locationType === 'Golf Course' && (
                <GolfCourseSelect onChange={handleGolfCourseChange} />
              )}
              {locationType === 'Postcode' && (
                <PostcodeSelect
                  onChange={(value) => setPostcode(value)}
                  onBlur={handlePostcodeChange}
                  selectedValue={postcode}
                />
              )}
            </div>
          )}

          <div className="flex-grow">
            <label className="block text-white font-bold text-sm lg:text-base">
              Max Distance:
            </label>
            <Select
              options={distanceOptions}
              onChange={handleDistanceChange}
              value={selectedDistance}
              styles={customStyles}
            />
          </div>

          {/*Number of Rounds*/}
          <div className="flex-grow">
            <label className="block text-white font-bold text-sm lg:text-base">
              # Rounds:
            </label>
            <Select
              options={formatOptions(
                [...Array(10).keys()].map((i) => (i + 1).toString()),
              )}
              onChange={(e) => setRounds(e.value)}
              styles={customStyles}
            />
          </div>

          <div className="flex-grow">
            <label className="block text-white font-bold text-sm lg:text-base">
              Golf Course Ranking:
            </label>
            <Select
              options={formatOptions(ratings)}
              value={formatOptions([rating])}
              onChange={(e) => setRating(e.value)}
              styles={customStyles}
            />
          </div>

          <button
            className="flex-grow lg:w-auto self-end h-[38px] py-1 px-4 bg-green-500 hover:bg-green-700 text-white font-bold rounded focus:outline-none focus:shadow-outline"
            onClick={handleSearch}
          >
            Search
          </button>
        </div>
        <div className="min-h-[100px]">
          {isLoading ? (
            <LoadingScreen type="spin" color="#000" />
          ) : (
            <GolfTripResults
              error={error}
              selectedCountrySearch={selectedCountrySearch}
              countryRankPrefix={countryRankPrefix}
              golfCourses={golfCourses}
              locationType={locationType}
            />
          )}
        </div>
      </section>
    </>
  );
}

export default TripSearchCriteria;
